import React, { useState } from "react";
import { ChevronDown, ChevronRight, FolderOpen, Plus, FileText } from "lucide-react";
import deskGlass from "../../styles/deskGlass";
import {
  getChildren,
  folderProgress,
  addChecklistItem,
  toggleTextDone,
  createProjectFolder,
} from "../../services/workspaceService";

const styles = deskGlass;

function ChecklistRow({
  item,
  allTexts,
  depth,
  locked,
  nestedOpen,
  onToggleNested,
  onToggleDone,
  onOpen,
  onAddChild,
}) {
  const isFolder = item.kind === "folder";
  const kids = isFolder ? getChildren(allTexts, item.id) : [];
  const prog = folderProgress(kids);
  const open = !!nestedOpen[item.id];

  return (
    <div style={{ marginLeft: depth ? depth * 12 : 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 0",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <button
          type="button"
          disabled={locked}
          onClick={() => onToggleDone(item)}
          aria-label={item.done ? "Marcar pendiente" : "Marcar hecho"}
          style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            border: item.done ? "none" : "2px solid rgba(255,255,255,0.35)",
            background: item.done ? "#4ade80" : "transparent",
            cursor: locked ? "not-allowed" : "pointer",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: locked ? 0.45 : 1,
          }}
        >
          {item.done && <span style={{ color: "#000", fontSize: 11, fontWeight: "bold" }}>✓</span>}
        </button>
        {isFolder ? (
          <button
            type="button"
            onClick={() => onToggleNested(item.id)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "rgba(255,255,255,0.5)",
              display: "flex",
            }}
            aria-label={open ? "Colapsar" : "Expandir"}
          >
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : item.img ? (
          <img
            src={item.img}
            alt=""
            width={14}
            height={14}
            style={{ borderRadius: 3, objectFit: "cover", flexShrink: 0 }}
          />
        ) : (
          <FileText size={12} color="rgba(255,255,255,0.35)" style={{ flexShrink: 0 }} />
        )}
        <button
          type="button"
          onClick={() => onOpen(item)}
          style={{
            flex: 1,
            textAlign: "left",
            background: "none",
            border: "none",
            color: item.done ? "rgba(255,255,255,0.35)" : "#fff",
            fontSize: "0.9rem",
            fontWeight: isFolder ? 600 : 500,
            textDecoration: item.done ? "line-through" : "none",
            cursor: "pointer",
            padding: 0,
          }}
        >
          {item.title || "Sin título"}
          {isFolder && kids.length > 0 && (
            <span style={{ marginLeft: 8, fontSize: "0.7rem", opacity: 0.45, fontWeight: 400 }}>
              {prog.done}/{prog.total}
            </span>
          )}
        </button>
      </div>
      {isFolder && open && (
        <div>
          {kids.map((c) => (
            <ChecklistRow
              key={c.id}
              item={c}
              allTexts={allTexts}
              depth={depth + 1}
              locked={locked}
              nestedOpen={nestedOpen}
              onToggleNested={onToggleNested}
              onToggleDone={onToggleDone}
              onOpen={onOpen}
              onAddChild={onAddChild}
            />
          ))}
          {!locked && (
            <AddItemRow onAdd={(title, asFolder) => onAddChild(item, title, asFolder)} />
          )}
        </div>
      )}
    </div>
  );
}

function AddItemRow({ onAdd }) {
  const [val, setVal] = useState("");
  const [asFolder, setAsFolder] = useState(false);
  const submit = () => {
    const t = val.trim();
    if (!t) return;
    onAdd(t, asFolder);
    setVal("");
    setAsFolder(false);
  };
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", padding: "8px 0 4px", flexWrap: "wrap" }}>
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
        placeholder={asFolder ? "Nueva carpeta…" : "Nuevo ítem…"}
        style={{
          flex: 1,
          minWidth: 120,
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 8,
          color: "#fff",
          padding: "6px 10px",
          fontSize: "0.85rem",
          outline: "none",
        }}
      />
      <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.7rem", opacity: 0.6, cursor: "pointer" }}>
        <input type="checkbox" checked={asFolder} onChange={(e) => setAsFolder(e.target.checked)} />
        carpeta
      </label>
      <button
        type="button"
        onClick={submit}
        disabled={!val.trim()}
        style={{
          ...styles.glassActionBtn,
          width: 36,
          height: 36,
          opacity: val.trim() ? 1 : 0.4,
        }}
      >
        <Plus size={16} />
      </button>
    </div>
  );
}

function ProjectCard({
  project,
  allTexts,
  locked,
  expanded,
  onToggleExpand,
  nestedOpen,
  onToggleNested,
  onToggleDone,
  onOpen,
  onAddChild,
  showBody,
  onToggleBody,
}) {
  const children = getChildren(allTexts, project.id);
  const prog = folderProgress(children);
  const peek = (project.content || "").replace(/<[^>]*>?/gm, "").trim().slice(0, 80);
  const isOpen = expanded === project.id;

  return (
    <section style={{ ...styles.glassSection, marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          cursor: "pointer",
        }}
        onClick={() => onToggleExpand(project.id)}
      >
        <FolderOpen size={18} color="#D4AF37" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "1rem", fontWeight: 700, color: "#fff" }}>{project.title}</div>
          <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)", marginTop: 2 }}>
            {prog.total ? `${prog.done}/${prog.total}` : "sin ítems"}
            {peek ? ` · ${peek}${peek.length >= 80 ? "…" : ""}` : ""}
          </div>
        </div>
        {isOpen ? <ChevronDown size={18} color="rgba(255,255,255,0.4)" /> : <ChevronRight size={18} color="rgba(255,255,255,0.4)" />}
      </div>

      {isOpen && (
        <div style={{ marginTop: 12 }} onClick={(e) => e.stopPropagation()}>
          {(project.content || "").trim() && (
            <div style={{ marginBottom: 10 }}>
              <button
                type="button"
                onClick={() => onToggleBody(project.id)}
                style={{
                  background: "none",
                  border: "none",
                  color: "rgba(255,255,255,0.45)",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  padding: 0,
                  marginBottom: 6,
                }}
              >
                {showBody[project.id] ? "Ocultar nota" : "Ver nota del proyecto"}
              </button>
              {showBody[project.id] && (
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "rgba(255,255,255,0.7)",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.45,
                    marginBottom: 8,
                  }}
                >
                  {(project.content || "").replace(/<[^>]*>?/gm, "").slice(0, 600)}
                </div>
              )}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <button
              type="button"
              onClick={() => onOpen(project)}
              style={{ ...styles.viewToggle, fontSize: "0.7rem", padding: "6px 10px" }}
            >
              Abrir proyecto
            </button>
          </div>
          {children.map((c) => (
            <ChecklistRow
              key={c.id}
              item={c}
              allTexts={allTexts}
              depth={0}
              locked={locked}
              nestedOpen={nestedOpen}
              onToggleNested={onToggleNested}
              onToggleDone={onToggleDone}
              onOpen={onOpen}
              onAddChild={onAddChild}
            />
          ))}
          {!locked && (
            <AddItemRow onAdd={(title, asFolder) => onAddChild(project, title, asFolder)} />
          )}
        </div>
      )}
    </section>
  );
}

export default function ProjectFolders({
  projects,
  allTexts,
  locked,
  onRefresh,
  onOpenText,
}) {
  const [expanded, setExpanded] = useState(null);
  const [nestedOpen, setNestedOpen] = useState({});
  const [showBody, setShowBody] = useState({});
  const [newTitle, setNewTitle] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (locked || !newTitle.trim()) return;
    setCreating(true);
    try {
      await createProjectFolder(newTitle.trim());
      setNewTitle("");
      await onRefresh();
    } finally {
      setCreating(false);
    }
  };

  const handleToggleDone = async (item) => {
    if (locked) return;
    await toggleTextDone(item.deskId, item.drawerId, item.notebookId, item.id);
    await onRefresh();
  };

  const handleAddChild = async (parent, title, asFolder) => {
    if (locked) return;
    await addChecklistItem(
      parent.deskId,
      parent.drawerId,
      parent.notebookId,
      parent.id,
      title,
      { asFolder }
    );
    await onRefresh();
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleCreate();
          }}
          disabled={locked || creating}
          placeholder="Nuevo proyecto…"
          style={{
            flex: 1,
            minWidth: 160,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 10,
            color: "#fff",
            padding: "10px 12px",
            fontSize: "0.95rem",
            outline: "none",
            opacity: locked ? 0.5 : 1,
          }}
        />
        <button
          type="button"
          onClick={handleCreate}
          disabled={locked || creating || !newTitle.trim()}
          style={{
            ...styles.glassActionBtn,
            padding: "0 14px",
            height: 42,
            display: "flex",
            alignItems: "center",
            gap: 6,
            opacity: locked || !newTitle.trim() ? 0.45 : 1,
          }}
        >
          <Plus size={16} />
          <span style={{ fontSize: "0.75rem", fontWeight: 700 }}>PROYECTO</span>
        </button>
      </div>

      {projects.length === 0 && (
        <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.9rem", lineHeight: 1.5 }}>
          Un proyecto es una nota-carpeta: checklist de notas hijas. Creá el primero arriba.
        </p>
      )}

      {projects.map((p) => (
        <ProjectCard
          key={p.id}
          project={p}
          allTexts={allTexts}
          locked={locked}
          expanded={expanded}
          onToggleExpand={(id) => setExpanded((prev) => (prev === id ? null : id))}
          nestedOpen={nestedOpen}
          onToggleNested={(id) =>
            setNestedOpen((prev) => ({ ...prev, [id]: !prev[id] }))
          }
          onToggleDone={handleToggleDone}
          onOpen={onOpenText}
          onAddChild={handleAddChild}
          showBody={showBody}
          onToggleBody={(id) =>
            setShowBody((prev) => ({ ...prev, [id]: !prev[id] }))
          }
        />
      ))}
    </div>
  );
}
